import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Loader2, Star, Camera, X, Image as ImageIcon, Eye } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

export const exampleImageUrl = (asset_id) => (asset_id ? `${API}/forms/example/${asset_id}` : null);

// Collapsible "See Example" pill + panel shown to form fillers for document-photo steps.
function ExampleHint({ example, pillLabel, testid }) {
  const [open, setOpen] = React.useState(false);
  if (!example || !example.asset_id) return null;
  return (
    <div className="mb-2" data-testid={`${testid}-example`}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testid}-example-pill`}
        className="inline-flex items-center gap-1.5 rounded-full border border-[#d9ccff] bg-white px-2.5 py-1 text-[11px] font-medium text-[#5b2fd6] hover:bg-[#F3EFFF] transition-colors">
        <Eye className="h-3 w-3" /> {pillLabel || "See Example"}
      </button>
      {open && (
        <div className="mt-2 rounded-xl border border-[#EEEFEF] bg-white p-2 max-w-xs" data-testid={`${testid}-example-panel`}>
          <img src={exampleImageUrl(example.asset_id)} alt="Example" className="w-full rounded-lg object-contain max-h-56" />
          {example.text && <p className="text-[11px] text-[#595D62] mt-1.5 leading-relaxed">{example.text}</p>}
        </div>
      )}
    </div>
  );
}

// The full field library available in the builder. `input` = collects a value.
export const FIELD_LIBRARY = [
  { group: "General fields", items: [
    { type: "name", label: "Name", input: true },
    { type: "email", label: "Email Address", input: true },
    { type: "text", label: "Simple Text", input: true },
    { type: "textarea", label: "Text Area", input: true },
    { type: "mask", label: "Mask Input", input: true },
    { type: "number", label: "Numeric Input", input: true },
    { type: "address", label: "Address", input: true },
    { type: "country", label: "Country List", input: true },
    { type: "phone", label: "Phone/Mobile", input: true },
    { type: "url", label: "Website URL", input: true },
    { type: "dropdown", label: "Dropdown", input: true, options: true },
    { type: "radio", label: "Radio Button", input: true, options: true },
    { type: "checkbox", label: "CheckBox", input: true, options: true },
    { type: "multiple_choice", label: "Multiple Choice", input: true, options: true },
    { type: "datetime", label: "Time & Date", input: true },
    { type: "document_photo", label: "Document Photo (Camera)", input: true },
    { type: "image", label: "Image Upload", input: true },
    { type: "file", label: "File Upload", input: true },
  ]},
  { group: "Advanced fields", items: [
    { type: "rating", label: "Ratings", input: true },
    { type: "range", label: "Range Slider", input: true },
    { type: "color", label: "Color Picker", input: true },
    { type: "grid", label: "Checkable Grid", input: true, options: true },
    { type: "richtext", label: "Rich Text Input", input: true },
    { type: "save_resume", label: "Save & Resume", input: false },
    { type: "terms", label: "Terms & Conditions", input: true },
    { type: "heading", label: "Section Heading", input: false },
  ]},
];

export const FIELD_META = Object.fromEntries(
  FIELD_LIBRARY.flatMap((g) => g.items).map((i) => [i.type, i])
);

export const fieldNeedsOptions = (type) => !!FIELD_META[type]?.options;
export const fieldIsInput = (type) => !!FIELD_META[type]?.input;

// Field types that can drive conditional visibility of other fields (option-based).
export const CONDITION_TRIGGER_TYPES = ["dropdown", "radio", "checkbox", "multiple_choice", "country"];

// The selectable values a controlling field offers (for building a condition rule).
export function fieldOptionValues(field) {
  if (!field) return [];
  if (field.type === "country") return COUNTRIES;
  return field.options || [];
}

// Is `field` visible given the current form values? A field with a `condition`
// ({ field, op:"is"|"is_not", value }) only shows when the rule is satisfied.
export function fieldVisible(field, values) {
  const c = field?.condition;
  if (!c || !c.field || c.value === undefined || c.value === "") return true;
  const v = values?.[c.field];
  const hit = Array.isArray(v) ? v.map(String).includes(String(c.value)) : String(v ?? "") === String(c.value);
  return c.op === "is_not" ? !hit : hit;
}

// Filter a field list down to those currently visible for the given values.
export function visibleFields(fields, values) {
  return (fields || []).filter((f) => fieldVisible(f, values));
}

// How many camera steps a document-photo field has (default 1).
export const docPhotoSteps = (field) => Math.max(1, Number(field?.config?.steps) || 1);

// Normalise a single step's value into an array of {asset_id, filename}.
export function docStepPhotos(stepVal) {
  if (!stepVal) return [];
  if (Array.isArray(stepVal.photos)) return stepVal.photos.filter((p) => p?.asset_id);
  if (stepVal.asset_id) return [{ asset_id: stepVal.asset_id, filename: stepVal.filename }]; // legacy single
  if (Array.isArray(stepVal)) return stepVal.filter((p) => p?.asset_id);
  return [];
}

// Central "is this value empty" check used for required-field validation.
export function isFieldEmpty(field, value) {
  if (field.type === "document_photo") {
    const steps = docPhotoSteps(field);
    if (!Array.isArray(value)) return true;
    for (let i = 0; i < steps; i++) { if (docStepPhotos(value[i]).length === 0) return true; }
    return false;
  }
  if (field.type === "image" || field.type === "file") return !value?.asset_id;
  return value === undefined || value === null || value === "" || (Array.isArray(value) && !value.length) || value === false;
}

export const COUNTRIES = ["Australia", "New Zealand", "United States", "United Kingdom", "Canada", "Ireland", "Singapore", "India", "Other"];

const CAPTURE_BTN = "flex items-center justify-center gap-2 rounded-xl border px-3 py-3 cursor-pointer text-sm font-medium transition-colors";

// The two capture buttons: Take a photo (opens camera on mobile) or Choose from files.
function CaptureButtons({ uploading, onFile, testid, multiple }) {
  return (
    <div data-testid={testid} className="grid grid-cols-2 gap-2">
      <label className={`${CAPTURE_BTN} border-[#723FFF] bg-[#EDE7FF] text-[#5b2fd6] hover:bg-[#e2d8ff]`}>
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Take a photo
        <input type="file" hidden accept="image/*" capture="environment" onChange={(e) => onFile(e.target.files)} />
      </label>
      <label className={`${CAPTURE_BTN} border-[#EEEFEF] bg-[#F6F6FD] text-[#595D62] hover:border-[#d3d4da]`}>
        <Upload className="h-4 w-4" /> Choose from files
        <input type="file" hidden accept="image/*" multiple={multiple} onChange={(e) => onFile(e.target.files)} />
      </label>
    </div>
  );
}

// A thumbnail chip for a captured photo, using a local object-URL preview when available.
function PhotoThumb({ photo, previewUrl, onRemove, testid }) {
  return (
    <div className="relative rounded-xl overflow-hidden border border-[#EEEFEF] bg-[#F6F6FD]" data-testid={testid}>
      {previewUrl
        ? <img src={previewUrl} alt={photo.filename || "photo"} className="h-24 w-full object-cover" />
        : <div className="h-24 w-full flex items-center justify-center"><ImageIcon className="h-6 w-6 text-[#c4c5cc]" /></div>}
      <button type="button" data-testid={`${testid}-remove`} onClick={onRemove} className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"><X className="h-3.5 w-3.5" /></button>
      <p className="px-2 py-1 text-[10px] text-[#595D62] truncate bg-white">{photo.filename || "Captured photo"}</p>
    </div>
  );
}

// A capture "slot" supporting single or multiple photos, with instant thumbnail previews.
function PhotoSlot({ photos, onChange, uploader, testid, multiple }) {
  const [uploading, setUploading] = React.useState(false);
  const [previews, setPreviews] = React.useState({}); // asset_id -> object URL
  const list = photos || [];

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || !uploader) return;
    const toAdd = multiple ? files : files.slice(0, 1);
    setUploading(true);
    try {
      const uploaded = [];
      const newPreviews = {};
      for (const file of toAdd) {
        const localUrl = URL.createObjectURL(file);
        try {
          const res = await uploader(file);
          uploaded.push({ asset_id: res.asset_id, filename: res.filename });
          newPreviews[res.asset_id] = localUrl;
        } catch { URL.revokeObjectURL(localUrl); toast.error("Couldn't upload that photo — please try again."); }
      }
      if (uploaded.length) {
        setPreviews((p) => ({ ...p, ...newPreviews }));
        onChange(multiple ? [...list, ...uploaded] : uploaded);
      }
    } finally { setUploading(false); }
  };

  const removeAt = (i) => {
    const ph = list[i];
    if (ph && previews[ph.asset_id]) { URL.revokeObjectURL(previews[ph.asset_id]); }
    onChange(list.filter((_, idx) => idx !== i));
  };

  const showButtons = multiple || list.length === 0;
  return (
    <div data-testid={testid}>
      {list.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-2">
          {list.map((ph, i) => <PhotoThumb key={ph.asset_id || i} photo={ph} previewUrl={previews[ph.asset_id]} onRemove={() => removeAt(i)} testid={`${testid}-photo-${i}`} />)}
        </div>
      )}
      {showButtons && <CaptureButtons uploading={uploading} onFile={handleFiles} testid={`${testid}-buttons`} multiple={multiple} />}
      {multiple && list.length > 0 && <p className="text-[11px] text-[#9FA1A4] mt-1.5">Add more pages with the buttons above, or remove any you don't need.</p>}
    </div>
  );
}


// Renders one field as an interactive input. `value` + `onChange(v)`.
export function FormFieldInput({ field, value, onChange, uploader }) {
  const [uploading, setUploading] = React.useState(false);
  const f = field;
  const base = "bg-[#F6F6FD] border-[#EEEFEF]";

  const doUpload = async (file) => {
    if (!file || !uploader) return;
    setUploading(true);
    try { const res = await uploader(file); onChange({ asset_id: res.asset_id, filename: res.filename }); }
    catch { toast.error("Couldn't upload that file — please try again."); }
    finally { setUploading(false); }
  };

  if (f.type === "heading") {
    return <div className="pt-2"><p className="font-display text-lg font-semibold text-[#06060C]">{f.label}</p>{f.help && <p className="text-sm text-[#9FA1A4] mt-0.5">{f.help}</p>}</div>;
  }
  if (f.type === "save_resume") return null;

  const label = (
    <label className="text-sm font-medium text-[#1c1c24] mb-1.5 block">
      {f.label}{f.required && <span className="text-rose-500 ml-0.5">*</span>}
      {f.help && <span className="block text-xs text-[#9FA1A4] font-normal mt-0.5">{f.help}</span>}
    </label>
  );

  let control = null;
  switch (f.type) {
    case "textarea":
    case "richtext":
      control = <Textarea data-testid={`ff-${f.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={4} placeholder={f.placeholder} className={`${base} resize-y`} />;
      break;
    case "number":
    case "range":
    case "rating":
      if (f.type === "rating") {
        control = <div className="flex items-center gap-1" data-testid={`ff-${f.id}`}>{[1,2,3,4,5].map((n) => <button type="button" key={n} onClick={() => onChange(n)} className={n <= (value || 0) ? "text-[#F59E0B]" : "text-[#d3d4d8]"}><Star className="h-6 w-6" fill={n <= (value || 0) ? "#F59E0B" : "none"} /></button>)}</div>;
      } else if (f.type === "range") {
        control = <div><input data-testid={`ff-${f.id}`} type="range" min="0" max="100" value={value || 0} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[#723FFF]" /><span className="text-xs text-[#9FA1A4]">{value || 0}</span></div>;
      } else {
        control = <Input data-testid={`ff-${f.id}`} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} className={base} />;
      }
      break;
    case "color":
      control = <input data-testid={`ff-${f.id}`} type="color" value={value || "#723FFF"} onChange={(e) => onChange(e.target.value)} className="h-10 w-16 rounded border border-[#EEEFEF] cursor-pointer" />;
      break;
    case "datetime":
      control = <Input data-testid={`ff-${f.id}`} type="datetime-local" value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} />;
      break;
    case "country":
      control = (
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={`ff-${f.id}`} className={base}><SelectValue placeholder="Select a country" /></SelectTrigger>
          <SelectContent className="bg-white border-[#EEEFEF]">{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      );
      break;
    case "dropdown":
      control = (
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={`ff-${f.id}`} className={base}><SelectValue placeholder={f.placeholder || "Select…"} /></SelectTrigger>
          <SelectContent className="bg-white border-[#EEEFEF]">{(f.options || []).map((o, i) => <SelectItem key={i} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      );
      break;
    case "radio":
    case "multiple_choice":
      control = <div className="space-y-1.5" data-testid={`ff-${f.id}`}>{(f.options || []).map((o, i) => (
        <label key={i} className="flex items-center gap-2 text-sm text-[#1c1c24] cursor-pointer"><input type="radio" name={f.id} checked={value === o} onChange={() => onChange(o)} className="accent-[#723FFF]" /> {o}</label>
      ))}</div>;
      break;
    case "checkbox":
    case "grid": {
      const arr = Array.isArray(value) ? value : [];
      const toggle = (o) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
      control = <div className="space-y-1.5" data-testid={`ff-${f.id}`}>{(f.options || []).map((o, i) => (
        <label key={i} className="flex items-center gap-2 text-sm text-[#1c1c24] cursor-pointer"><input type="checkbox" checked={arr.includes(o)} onChange={() => toggle(o)} className="accent-[#723FFF]" /> {o}</label>
      ))}</div>;
      break;
    }
    case "terms":
      control = <label className="flex items-start gap-2 text-sm text-[#1c1c24] cursor-pointer" data-testid={`ff-${f.id}`}><input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} className="accent-[#723FFF] mt-0.5" /> <span>{f.placeholder || "I agree to the terms and conditions"}</span></label>;
      break;
    case "document_photo": {
      const steps = docPhotoSteps(f);
      const labels = f.config?.step_labels || [];
      const examples = f.config?.step_examples || [];
      const showExample = !!f.config?.show_example;
      const pillLabel = f.config?.example_pill_label || "See Example";
      const multi = !!f.config?.multi;
      const arr = Array.isArray(value) ? value : [];
      const setStep = (i, photos) => {
        const next = Array.from({ length: steps }).map((_, idx) => arr[idx] || null);
        next[i] = { step_label: labels[i] || (steps > 1 ? `Photo ${i + 1}` : ""), photos };
        onChange(next);
      };
      control = (
        <div className="space-y-3" data-testid={`ff-${f.id}`}>
          {Array.from({ length: steps }).map((_, i) => {
            const stepLabel = labels[i] || (steps > 1 ? `Photo ${i + 1}` : "");
            return (
              <div key={i} className="rounded-xl border border-dashed border-[#d9ccff] bg-[#FCFBFF] p-3">
                {stepLabel && <p className="text-xs font-medium text-[#5b2fd6] mb-2 flex items-center gap-1.5"><Camera className="h-3.5 w-3.5" /> {stepLabel}</p>}
                {showExample && <ExampleHint example={examples[i]} pillLabel={pillLabel} testid={`ff-${f.id}-step-${i}`} />}
                <PhotoSlot photos={docStepPhotos(arr[i])} onChange={(photos) => setStep(i, photos)} uploader={uploader} multiple={multi} testid={`ff-${f.id}-step-${i}`} />
              </div>
            );
          })}
        </div>
      );
      break;
    }
    case "image":
      if (f.config?.capture) {
        const imgPhotos = value?.asset_id ? [{ asset_id: value.asset_id, filename: value.filename }] : [];
        control = <PhotoSlot photos={imgPhotos} onChange={(photos) => onChange(photos[0] || null)} uploader={uploader} multiple={false} testid={`ff-${f.id}`} />;
        break;
      }
      // falls through to the standard upload UI below
    case "file":
      control = (
        <div data-testid={`ff-${f.id}`}>
          <label className={`flex items-center gap-2 rounded-xl border border-dashed border-[#d3d4da] ${base} px-3 py-3 cursor-pointer hover:border-[#723FFF] text-sm text-[#595D62]`}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {value?.filename ? <span className="text-[#06060C] truncate">{value.filename}</span> : <span>{f.type === "image" ? "Upload an image" : "Upload a file"}</span>}
            <input type="file" hidden accept={f.type === "image" ? "image/*" : undefined} onChange={(e) => doUpload(e.target.files?.[0])} />
          </label>
        </div>
      );
      break;
    case "address":
      control = <Textarea data-testid={`ff-${f.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={2} placeholder={f.placeholder || "Street, City, State, Postcode"} className={`${base} resize-y`} />;
      break;
    default: // name, email, text, mask, phone, url
      control = <Input data-testid={`ff-${f.id}`} type={f.type === "email" ? "email" : f.type === "url" ? "url" : "text"} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} className={base} />;
  }

  return <div>{label}{control}</div>;
}
